import { useForm, SubmitHandler, Path } from "react-hook-form";
import { preflightUsernameService } from "../services/apiServices";
import { useTranslation } from "react-i18next";

interface FormData {
  [key: string]: any;
}

interface HookOptions {
  [key: string]: string | {
    [key: string]: string
  } 
}

export const useBoingoForm = <T extends FormData>(options?: HookOptions) => {
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    setError,
    clearErrors,
    watch,
    trigger,
    setValue,
    reset,
  } = useForm<T>({
    mode: "onChange",
    ...options || {}
  });

  const handleUsernameBlur = async (
    event: React.FocusEvent<HTMLInputElement>
  ) => {
    const username = event.target.value;
    if (username) {
      await trigger("username" as Path<T>);

      const currentErrors = errors.username;

      // Only proceed with availability check if there are no regex errors
      if (!currentErrors) {
        try {
          const response = await preflightUsernameService({ username });
          if (response.status === 200) {
            clearErrors("username" as Path<T>);
          }
        } catch (error) {
          console.error("Username is not available:", error);
          setError("username" as Path<T>, {
            type: "manual",
            message: t(
              "form_errors.username_not_available",
              "Username is not available"
            ),
          });
        }
      }
    }
  };

  const handleDeviceBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { target } = e;
    const humanFriendlyName = target.name.includes("mac")
      ? "MAC address"
      : target.name.includes("name")
      ? "Device Name"
      : "Device Type";

    // Check if the field is empty
    if (!e.target.value) {
      setError(target.name as Path<T>, {
        type: "manual",
        message: `${humanFriendlyName || "This field"} is required.`,
      });
    } else {
      clearErrors(target.name as Path<T>);
    }
  };

  return {
    control,
    handleSubmit: handleSubmit as (
      onSubmit: SubmitHandler<T>
    ) => (e?: React.BaseSyntheticEvent) => Promise<void>,
    errors,
    isValid,
    watch,
    handleUsernameBlur,
    handleDeviceBlur,
    trigger,
    setValue,
    setError,
    clearErrors,
    reset,
  };
};
